import { cognitoApi } from '../cognitoApi';
import { CognitoAPIResponse } from '../types';
import { attachId, attachIds } from '../utils';

import {
  Topic, TopicGroup,
} from './types';

export const topicsApi = cognitoApi.injectEndpoints({
  endpoints: (build) => ({
    getTopicGroups: build.query<CognitoAPIResponse<TopicGroup[]>, { subject: string }>({
      query(arg) {
        const { subject } = arg;
        return {
          url: `topics/${subject}/groups`,
          method: 'GET',
        };
      },
      providesTags: (result) => (result
        ? [...result.data.map(({ id }) =>
          ({ type: 'TopicGroups' as const, id })), 'TopicGroups']
        : ['TopicGroups']),
      transformResponse(response: any) {
        return attachIds(response, (entry: any) => entry.groupId);
      },
    }),
    getTopicGroup: build.query<CognitoAPIResponse<TopicGroup>,
    { subject: string, groupId: string }>({
      query(arg) {
        const { subject, groupId } = arg;
        return {
          url: `topics/${subject}/group/${groupId}`,
          method: 'GET',
        };
      },
      providesTags: (result, error, arg) => [{
        type: 'TopicGroups',
        id: arg.subject,
      }],
      transformResponse(response: any) {
        return attachId(response, (entry: any) => entry.groupId);
      },
    }),
    getTopics: build.query<CognitoAPIResponse<Topic[]>, { subject: string, search: string }>({
      query(arg) {
        const { subject, search } = arg;
        return {
          url: `topics/${subject}/query`,
          method: 'GET',
          params: {
            search,
          },
        };
      },
      providesTags: (result) => (result
        ? [...result.data.map(({ id }) =>
          ({ type: 'Topics' as const, id })), 'Topics']
        : ['Topics']),
      transformResponse(response: any) {
        return attachIds(response, (entry: any) => entry.topicId);
      },
    }),
    newTopicGroup: build.mutation<any, { attributes: any }>({
      query(arg) {
        const { attributes } = arg;

        return {
          url: 'topics/group/new',
          body: attributes,
          method: 'POST',
        };
      },
      invalidatesTags: (result) => [{
        type: 'TopicGroups',
        id: result?.data.subject,
      }],
    }),
    newTopic: build.mutation<any, { subject: string, topicGroup: string, label: string }>({
      query(arg) {
        const { label, subject, topicGroup } = arg;

        return {
          url: 'topics/new',
          body: {
            subject,
            topicGroup,
            label,
          },
          method: 'POST',
        };
      },
      invalidatesTags: (result, error, arg) => ([{
        type: 'TopicGroups',
        id: arg?.subject,
      },
      'Topics']),
    }),
    updateTopicGroup: build.mutation<any, { subject: string, groupId: string, attributes: any }>({
      query(arg) {
        const { attributes, subject, groupId } = arg;

        return {
          url: `topics/${subject}/group/${groupId}/update`,
          body: attributes,
          method: 'PUT',
        };
      },
      invalidatesTags: (result, error, arg) => ([{
        type: 'TopicGroups',
        id: arg?.subject,
      },
      {
        type: 'Topics',
      }]),
    }),
    deleteTopic: build.mutation<any, { subject: string, topicId: string }>({
      query(arg) {
        const { subject, topicId } = arg;

        return {
          url: `topics/${subject}/${topicId}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (result, error, arg) => ([{
        type: 'TopicGroups',
        id: arg?.subject,
      },
      {
        type: 'Topics',
        id: arg?.topicId,
      }]),
    }),
    deleteTopicGroup: build.mutation<any, { subject: string, groupId: string }>({
      query(arg) {
        const { subject, groupId } = arg;

        return {
          url: `topics/${subject}/group/${groupId}`,
          method: 'DELETE',
        };
      },
      invalidatesTags: (result, error, arg) => ([{
        type: 'TopicGroups',
        id: arg?.subject,
      }]),
    }),
  }),
});

export const {
  useGetTopicGroupsQuery,
  useNewTopicGroupMutation,
  useNewTopicMutation,
  useUpdateTopicGroupMutation,
  useGetTopicsQuery,
  useGetTopicGroupQuery,
  useDeleteTopicGroupMutation,
  useDeleteTopicMutation,
} = topicsApi;
